// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { updateXCheckoutState } from "@conklin/App/Handlers/Checkout/xCheckout";
import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import RadioGroup, { RadioGroupProps } from "@conklin/MobiusOverrides/RadioGroup/RadioGroup";
import AddressInfoDisplay from "@conklin/Overrides/Components/AddressInfoDisplay";
import { OnePageCheckoutFormContext } from "@conklin/Overrides/Pages/OnePageCheckoutPage";
import { xValidateOnSubmit } from "@conklin/XDL/Utilities";
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import saveShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import updateShipTo from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import BillToSelectorModal, {
    BillToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import ShipToSelectorModal, {
    ShipToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio from "@insite/mobius/Radio";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;

    // XNG-Changes
    let oneTimeAddress;
    let canUseBillToAsShipTo = false;
    let isShipToSameAsBillTo = false;
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cart.billToId === cart.shipToId;
        canUseBillToAsShipTo = shipTos!.some(shipTo => shipTo.id === cart.billToId);
    }
    const session = getSession(state);
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    const pickUpWarehouse = cart?.defaultWarehouse || session.pickUpWarehouse;
    let newAddress;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
    }

    return {
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isSavingShipping: state.pages.onePageCheckout.isSavingShipping,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        // <XNG-Changes>
        shipTosDataView,
        // shipTosDataView: cart
        //     ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        //     : undefined,
        // </XNG-Changes>
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod,
        pickUpWarehouse,
        newAddress,
        // XNG-Changes
        currentBillToId: cart?.billToId,
        shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
        useOneTimeAddress: state.xCheckout.useOneTimeAddress,
        oneTimeAddress,
        canUseBillToAsShipTo,
        isShipToSameAsBillTo,
        isEditing: state.xCheckout.isAddressEditing,
        isAuthenticated: xIsAuthenticated(state),
        lockCustomerSelection: getSettingsCollection(state).customSettings?.lockCustomerSelection,
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    // XNG-Changes
    updateXCheckoutState,
    updateShipTo,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface OnePageCheckoutShippingSectionStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    saveBillToButton?: ButtonPresentationProps;
}

export const onePageCheckoutShippingSectionStyles: OnePageCheckoutShippingSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: { weight: 600 },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = onePageCheckoutShippingSectionStyles;

const OnePageCheckoutShippingSection = ({
    isShippingAddressUpdateRequired,
    isBillingAddressUpdateRequired,
    isBillingAddressTheSameAsShipping,
    isSavingShipping,
    cart,
    shipToState,
    billToState,
    currentUserIsGuest,
    countries,
    shipTosDataView,
    shipToAddressFields,
    billToAddressFields,
    fulfillmentMethod,
    pickUpWarehouse,
    newAddress,
    theme,
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    // XNG-Changes
    currentBillToId,
    shippingAddressFormState,
    useOneTimeAddress,
    oneTimeAddress,
    canUseBillToAsShipTo,
    isShipToSameAsBillTo,
    isEditing,
    isAuthenticated,
    updateXCheckoutState,
    updateShipTo,
    lockCustomerSelection,
}: Props) => {
    const context = useContext(OnePageCheckoutFormContext);
    // XNG-Changes
    // const [isEditing, setIsEditing] = useState(false);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const [isBillToSelectorModalOpen, setIsBillToSelectorModalOpen] = useState(false);
    const [isAddressSaved, setIsAddressSaved] = useState(false);

    const shipTo = shipToState.value;
    const billTo = billToState.value;
    const dispatch = useAppDispatch();
    useEffect(() => {
        // if authenticated and FulfillmentMethod is shipTo set isAddressEditing to true only if isShippingAddressUpdateRequired
        if (
            fulfillmentMethod === FulfillmentMethod.Ship &&
            isAuthenticated &&
            !shippingAddressFormState?.address?.oneTimeAddress &&
            shipToState?.value
        ) {
            if (!isBillingAddressTheSameAsShipping && isShippingAddressUpdateRequired) {
                updateXCheckoutState({ isAddressEditing: true });
            }
        } else if (
            isAuthenticated &&
            isShippingAddressUpdateRequired &&
            shippingAddressFormState?.address?.oneTimeAddress
        ) {
            updateXCheckoutState({ isAddressEditing: true });
        }
        // if Unauthenticated set isAddressEditing to true if isShippingAddressUpdateRequired or isBillingAddressUpdateRequired
        else if (!isAuthenticated) {
            if (isBillingAddressUpdateRequired || isShippingAddressUpdateRequired) {
                updateXCheckoutState({ isAddressEditing: true });
            }
        }
    }, [isShippingAddressUpdateRequired, isBillingAddressUpdateRequired, shipToState?.value]);

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart) {
        return null;
        // XNG-Changes: removed from here since we have page-level spinner
        // <StyledWrapper {...styles.loadingWrapper}>
        //     <LoadingSpinner />
        // </StyledWrapper>
    }

    if (!countries || (!shipTo && !newAddress) || !billTo) {
        return null;
    }

    const useShippingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: value });
    };

    const editShippingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const editBillingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsBillToSelectorModalOpen(true);
    };

    const billToSelectorModalCloseHandler = () => {
        setIsBillToSelectorModalOpen(false);
    };

    const editPickUpWarehouseClickHandler = () => {
        setStorePickupOverlayIsOpen({ isOpen: true });
    };

    const titleEditClickHandler = () => {
        // XNG-Changes: add shipTo.oneTimeAddress
        if (currentUserIsGuest || shipTo?.oneTimeAddress) {
            // XNG-Changes
            // setIsEditing(true);
            updateXCheckoutState({ isAddressEditing: true });
            setIsAddressSaved(false);
        } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
            setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true });
        }
    };

    const saveClickHandler = async () => {
        context.formSubmitAttempted = true;

        // XNG Changes
        const isBillingValid = context.formIds?.billingFormId
            ? xValidateOnSubmit(context.formIds.billingFormId).length === 0
            : true;
        const isShippingValid = context.formIds?.shippingFormId
            ? xValidateOnSubmit(context.formIds.shippingFormId).length === 0
            : true;

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        // XNG-Changes: add isBillingValid && isShippingValid
        if (results.every(o => o) && isBillingValid && isShippingValid) {
            saveShipping({
                onSuccess: () => {
                    // XNG-Changes
                    // setIsEditing(false);
                    setPaymentDetailsState({ paymentDetailsState: "active" });
                    dispatch({
                        type: "Pages/OnePageCheckout/SetIsBillingAddressUpdateRequired",
                        isBillingAddressUpdateRequired: false,
                    });
                    dispatch({
                        type: "Pages/OnePageCheckout/SetIsShippingAddressUpdateRequired",
                        isShippingAddressUpdateRequired: false,
                    });
                    updateXCheckoutState({ isAddressEditing: false });
                    setIsAddressSaved(true);
                },
            });
        } else {
            scrollToFirstFormError();
        }
    };

    // <XNG-Changes>
    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        oneTimeAddress &&
            updateShipTo({
                billToId: cart?.billToId || "",
                shipTo: oneTimeAddress,
                isEdit: true,
                onSuccess: hasCartlines => {
                    updateXCheckoutState({ isAddressEditing: true });
                },
            });
    };

    const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsBillingAddressTheSameAsShipping({
            isBillingAddressTheSameAsShipping: value,
        });
    };

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        if (event.target.value === "onetime") {
            oneTimeAddress &&
                updateShipTo({
                    billToId: cart?.billToId || "",
                    shipTo: oneTimeAddress,
                    isEdit: true,
                    onSuccess: hasCartlines => {
                        updateXCheckoutState({ isAddressEditing: true });
                        setIsBillingAddressTheSameAsShipping({
                            isBillingAddressTheSameAsShipping: false,
                        });
                    },
                });
        } else if (event.target.value === "billing") {
            setIsBillingAddressTheSameAsShipping({
                isBillingAddressTheSameAsShipping: true,
                onComplete: () => {
                    saveShipping({
                        onSuccess: () => {
                            setPaymentDetailsState({ paymentDetailsState: "active" });
                            updateXCheckoutState({ isAddressEditing: false });
                        },
                    });
                },
            });
        }
    };
    const oneTimeUseBillingElement = (
        <>
            {oneTimeAddress && canUseBillToAsShipTo && !currentUserIsGuest ? (
                <RadioGroup
                    value={
                        shippingAddressFormState
                            ? shippingAddressFormState.address.oneTimeAddress
                                ? "onetime"
                                : shippingAddressFormState.address.id === currentBillToId
                                  ? "billing"
                                  : ""
                            : ""
                    }
                    onChangeHandler={addressTypeChangeHandler}
                >
                    <Radio value="onetime" data-test-selector="checkoutShipping_useOneTimeAddress">
                        {translate("Ship to One-Time Address")}
                    </Radio>
                    <Radio value="billing" data-test-selector="checkoutShipping_useBillingAddress">
                        {translate("Ship to Billing Address")}
                    </Radio>
                </RadioGroup>
            ) : (
                <>
                    {oneTimeAddress && !currentUserIsGuest && (
                        <CheckboxGroup>
                            <Checkbox onChange={oneTimeAddressChangeHandler} checked={useOneTimeAddress}>
                                {translate("Ship to One-Time Address")}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                    {canUseBillToAsShipTo &&
                        (cart.fulfillmentMethod === FulfillmentMethod.PickUp ||
                            (cart.fulfillmentMethod === FulfillmentMethod.Ship && !isAddressSaved)) && (
                            <CheckboxGroup>
                                <Checkbox
                                    onChange={useBillingAddressChangeHandler}
                                    checked={isBillingAddressTheSameAsShipping}
                                >
                                    {translate("Ship to Billing Address")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                </>
            )}
        </>
    );
    // </XNG-Changes>

    // XNG-Changes: structur
    return (
        // <LoadingOverlay loading={isSavingShipping}>
        <StyledWrapper {...styles.container} data-test-selector="shippingAndBillingSection">
            <Typography {...checkoutSharedStyles.containerTitle}>
                {translate("Addresses")}
                {currentUserIsGuest && (isAddressSaved || !isEditing) && (
                    <Link {...checkoutSharedStyles.sectionTitleLink} onClick={titleEditClickHandler}>
                        {translate("Edit")}
                        <VisuallyHidden>{translate("Edit")}</VisuallyHidden>
                    </Link>
                )}
            </Typography>

            {/* 
                {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                    <>
                        <GridItem {...styles.titleGridItem}>
                            <Typography as="h2" {...styles.titleText}>
                                {translate("Pick Up Address")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.titleEditGridItem}>
                            <Link {...styles.titleEditLink} onClick={editPickUpWarehouseClickHandler}>
                                {translate("Edit")}
                            </Link>
                        </GridItem>
                        <GridItem {...styles.pickUpGridItem}>
                            <AddressInfoDisplay
                                {...pickUpWarehouse}
                                companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        </GridItem>
                    </>
                )}
                <StyledWrapper {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {fulfillmentMethod === FulfillmentMethod.Ship
                            ? translate("Shipping")
                            : translate("Billing Address")}
                    </Typography>
                </StyledWrapper> 
            */}
            <StyledWrapper {...checkoutSharedStyles.container}>
                {currentUserIsGuest && fulfillmentMethod === FulfillmentMethod.Ship ? (
                    <>
                        {/* XNG-Changes: data-test-selectors */}
                        <StyledWrapper {...checkoutSharedStyles.section} data-test-selector="BillingAddress">
                            <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                {translate("Billing Address")}
                            </Typography>

                            {!isAddressSaved && billToAddressFields && (
                                <>
                                    <StyledWrapper {...styles.billToGridItem}>
                                        <BillingAddressForm
                                            countries={countries}
                                            fieldDisplay={billToAddressFields}
                                            extendedStyles={styles.billingAddressForm}
                                        />
                                    </StyledWrapper>
                                </>
                            )}
                            {billTo && isAddressSaved && (
                                <StyledWrapper {...checkoutSharedStyles.section}>
                                    <StyledWrapper {...styles.billToGridItem}>
                                        {/* {...billingAddressFormState.address} */}
                                        <AddressInfoDisplay
                                            {...billTo}
                                            state={billTo.state ? billTo.state.abbreviation : undefined}
                                            country={billTo.country ? billTo.country.abbreviation : undefined}
                                            extendedStyles={styles.addressInfoDisplay}
                                            // XNG-Changes
                                            attention=""
                                            displayCustomerNumber={false}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                            {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                                fields: {},
                            })}
                        </StyledWrapper>
                        {/* XNG-Changes: data-test-selectors */}
                        <StyledWrapper {...checkoutSharedStyles.section} data-test-selector="ShippingAddress">
                            <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                {translate("Shipping Address")}
                            </Typography>
                            {fulfillmentMethod === FulfillmentMethod.Ship && (
                                <StyledWrapper {...styles.useShippingAddressGridItem}>
                                    {oneTimeUseBillingElement}
                                    {isAddressSaved && shipTo && (
                                        // {...shippingAddressFormState?.address}
                                        <AddressInfoDisplay
                                            {...shipTo}
                                            state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                            country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                            extendedStyles={styles.addressInfoDisplay}
                                            displayCustomerNumber={false}
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                            {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && !isAddressSaved && (
                                <StyledWrapper {...styles.shipToGridItem}>
                                    <ShippingAddressForm
                                        countries={countries}
                                        fieldDisplay={shipToAddressFields}
                                        extendedStyles={styles.shippingAddressForm}
                                        // XNG-Changes
                                        {...{ formDisabled: isBillingAddressTheSameAsShipping }}
                                    />
                                </StyledWrapper>
                            )}
                            {!isAddressSaved && (
                                <StyledWrapper {...styles.saveButtonGridItem}>
                                    <Button
                                        {...styles.saveButton}
                                        onClick={saveClickHandler}
                                        data-test-selector="saveAndContinueButton"
                                    >
                                        {/* XNG-Changes */}
                                        {translate("Save & Estimate Tax")}
                                    </Button>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>
                ) : (
                    <>
                        {/* XNG-Changes: data-test-selectors */}
                        <StyledWrapper {...checkoutSharedStyles.section} data-test-selector="BillingAddress">
                            <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                {translate("Billing Address")}
                                {/* XNG-Changes */}
                                {!currentUserIsGuest && !lockCustomerSelection && (
                                    <>
                                        <Link
                                            {...checkoutSharedStyles.sectionTitleLink}
                                            onClick={editBillingAddressClickHandler}
                                            data-test-selector="editBillingAddressButton"
                                        >
                                            {translate("Select Saved Addresses")}
                                            <VisuallyHidden>{translate("Billing Address")}</VisuallyHidden>
                                        </Link>
                                        <BillToSelectorModal
                                            isOpen={isBillToSelectorModalOpen}
                                            onModalClose={billToSelectorModalCloseHandler}
                                            extendedStyles={styles.billToSelectorModal}
                                        />
                                    </>
                                )}
                            </Typography>

                            {billToAddressFields && isEditing && !isAuthenticated && (
                                <>
                                    <StyledWrapper {...styles.billToGridItem}>
                                        <BillingAddressForm
                                            countries={countries}
                                            fieldDisplay={billToAddressFields}
                                            extendedStyles={styles.billingAddressForm}
                                        />
                                    </StyledWrapper>
                                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp && (
                                        <StyledWrapper {...styles.saveButtonGridItem}>
                                            <Button
                                                {...styles.saveBillToButton}
                                                onClick={saveClickHandler}
                                                data-test-selector="saveAndContinueButton"
                                            >
                                                {/* XNG-Changes */}
                                                {translate("Save & Estimate Tax")}
                                            </Button>
                                        </StyledWrapper>
                                    )}
                                </>
                            )}

                            {billTo && (!isEditing || (isEditing && isAuthenticated)) && (
                                <StyledWrapper {...checkoutSharedStyles.section}>
                                    <StyledWrapper {...styles.billToGridItem} data-test-selector="BillingAddress">
                                        <AddressInfoDisplay
                                            {...billTo}
                                            state={billTo.state ? billTo.state.abbreviation : undefined}
                                            country={billTo.country ? billTo.country.abbreviation : undefined}
                                            extendedStyles={styles.addressInfoDisplay}
                                            // XNG-Changes
                                            attention=""
                                            displayCustomerNumber={false}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                            {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                                fields: {},
                            })}
                        </StyledWrapper>
                        {isEditing &&
                            (!isShipToSameAsBillTo || !shipTo) &&
                            fulfillmentMethod === FulfillmentMethod.Ship && (
                                // XNG-Changes: data-test-selectors
                                <StyledWrapper {...checkoutSharedStyles.section} data-test-selector="ShippingAddress">
                                    <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                        {translate("Shipping Address")}
                                        {/* XNG-Changes */}
                                        {!currentUserIsGuest && !lockCustomerSelection && (
                                            <>
                                                <Link
                                                    {...checkoutSharedStyles.sectionTitleLink}
                                                    onClick={editShippingAddressClickHandler}
                                                    data-test-selector="editShippingAddressButton"
                                                >
                                                    {translate("Select Saved Addresses")}
                                                    <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                                </Link>
                                                <ShipToSelectorModal
                                                    isOpen={isShipToSelectorModalOpen}
                                                    onModalClose={shipToSelectorModalCloseHandler}
                                                    extendedStyles={styles.shipToSelectorModal}
                                                />
                                            </>
                                        )}
                                    </Typography>
                                    {fulfillmentMethod === FulfillmentMethod.Ship && (
                                        <StyledWrapper {...styles.useShippingAddressGridItem}>
                                            {oneTimeUseBillingElement}
                                        </StyledWrapper>
                                    )}
                                    {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && (
                                        <StyledWrapper {...styles.shipToGridItem}>
                                            <ShippingAddressForm
                                                countries={countries}
                                                fieldDisplay={shipToAddressFields}
                                                extendedStyles={styles.shippingAddressForm}
                                                // XNG-Changes
                                                {...{ formDisabled: isBillingAddressTheSameAsShipping }}
                                            />
                                        </StyledWrapper>
                                    )}
                                    <StyledWrapper {...styles.saveButtonGridItem}>
                                        <Button
                                            {...styles.saveButton}
                                            onClick={saveClickHandler}
                                            data-test-selector="saveAndContinueButton"
                                        >
                                            {/* XNG-Changes */}
                                            {translate("Save & Estimate Tax")}
                                        </Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                        {(!isEditing || (isShipToSameAsBillTo && (shipTo || newAddress))) &&
                            fulfillmentMethod === FulfillmentMethod.Ship && (
                                // XNG-Changes: data-test-selectors
                                <StyledWrapper {...checkoutSharedStyles.section} data-test-selector="ShippingAddress">
                                    <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                        {translate("Shipping Address")}
                                        {/* XNG-Changes */}
                                        {!currentUserIsGuest && !lockCustomerSelection && (
                                            <>
                                                <Link
                                                    {...checkoutSharedStyles.sectionTitleLink}
                                                    onClick={editShippingAddressClickHandler}
                                                    data-test-selector="editShippingAddressButton"
                                                >
                                                    {translate("Select Saved Addresses")}
                                                    <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                                </Link>
                                                <ShipToSelectorModal
                                                    isOpen={isShipToSelectorModalOpen}
                                                    onModalClose={shipToSelectorModalCloseHandler}
                                                    extendedStyles={styles.shipToSelectorModal}
                                                />
                                            </>
                                        )}
                                    </Typography>

                                    <StyledWrapper {...styles.shipToGridItem}>
                                        {oneTimeUseBillingElement}
                                        {shipTo && (
                                            <AddressInfoDisplay
                                                {...shipTo}
                                                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                                country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                                extendedStyles={styles.addressInfoDisplay}
                                                displayCustomerNumber={false}
                                            />
                                        )}
                                    </StyledWrapper>
                                    {shipTo && shipTo.oneTimeAddress && !isShipToSameAsBillTo && (
                                        <Link {...checkoutSharedStyles.editLink} onClick={titleEditClickHandler}>
                                            {translate("Edit Address")}
                                            <VisuallyHidden>{translate("Edit Address")}</VisuallyHidden>
                                        </Link>
                                    )}
                                </StyledWrapper>
                            )}
                        {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                            <StyledWrapper {...checkoutSharedStyles.section}>
                                <Typography as="h2" {...checkoutSharedStyles.sectionTitle}>
                                    {translate("Pick Up Address")}
                                    <Link {...styles.titleEditLink} onClick={editPickUpWarehouseClickHandler}>
                                        {translate("Find Locations")}
                                    </Link>
                                </Typography>

                                <StyledWrapper {...styles.pickUpGridItem}>
                                    <AddressInfoDisplay
                                        {...pickUpWarehouse}
                                        companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                        extendedStyles={styles.addressInfoDisplay}
                                        displayCustomerNumber={false}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
        // </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutShippingSection)),
    definition: {
        displayName: "Shipping Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
